import { ApiRequest } from '../../api/request';
import { DataResponse, FileResponse } from '@fino/ng1-response/lib';
import { LoginResponse } from '../../auth/loginResponse';
import {
  TicketType,
  TicketRequestType,
  ProductType,
} from '../../../components/ticket/ticket.constants';
import _ from 'lodash';

export const TicketTypeMapping = {
  [TicketType.Transferring]: TicketRequestType.Outgoing,
  [TicketType.Receiving]: TicketRequestType.Incoming,
};

export class TicketsProvider {
  constructor() {
    'ngInject';
  }

  $get(ticketsDataService) {
    'ngInject';
    return ticketsDataService;
  }
}

export class TicketsService extends ApiRequest {
  constructor($http, $q, appConfig, Upload, userService, fileResponseService) {
    'ngInject';
    super({ $http, $q, appConfig, Upload, userService, fileResponseService });
  }

  get ticketTypeMap() {
    return TicketTypeMapping;
  }

  getTicketType(ticketType) {
    return _.toLower(this.ticketTypeMap[ticketType] || ticketType);
  }

  createOrUpdate(data, ticketId, force, silent) {
    if (ticketId) {
      return this.$http({
        method: 'POST',
        data,
        url: `${this.api}/aid/requests/${ticketId}`,
        error: { silent },
      }).then(DataResponse.transform('data.id'));
    }

    return this.create(data, force, silent);
  }

  downloadPreviewOfAccountswitchDatesProtocol(data) {
    return this.$http({
      method: 'POST',
      url: `${this.api}/aid/switchDatesProtocol/preview`,
      data,
    }).then(DataResponse.transform('data'));
  }

  downloadPaymentPartnerOverviewDocument(id, data) {
    return this.$http({
      method: 'POST',
      url: `${this.api}/aid/requests/incoming/${id}/paymentPartnerOverview`,
      data,
    }).then(DataResponse.transform('data'));
  }

  changeBranchId(data, ticketId) {
    if (ticketId) {
      return this.$http({
        method: 'PUT',
        data,
        url: `${this.api}/aid/requests/${ticketId}/branch`,
      }).then(DataResponse.transform('data.id'));
    }
  }

  create(data, force, silent) {
    return this.$http({
      method: 'PUT',
      data,
      url: `${this.api}/aid/requests?force=${!!force}`,
      error: { silent },
    })
      .then(LoginResponse.loginTo(this.userService))
      .then(DataResponse.transform('data.id'));
  }

  createOutgoing(data) {
    return this.$http({
      method: 'PUT',
      data,
      url: `${this.api}/aid/requests/outgoing`,
    }).then(DataResponse.transform('data.id'));
  }

  createOutgoingWithSignedWarrant(file, entryDate) {
    return this.Upload.upload({
      url: `${this.api}/aid/requests/outgoing`,
      data: { file, entryDate },
    }).then(DataResponse.transform('data'));
  }

  acceptOutgoing(ticketId, data) {
    return this.$http({
      method: 'POST',
      data,
      url: `${this.api}/aid/requests/${ticketId}/acceptOutgoingAidRequest`,
    }).then(DataResponse.transform('data.id'));
  }

  confirmOutgoing(ticketId, data) {
    return this.$http({
      method: 'PUT',
      data,
      url: `${this.api}/aid/requests/outgoing/${ticketId}`,
    }).then(DataResponse.transform('data.id'));
  }

  setAccountCloseDate(ticketId, data) {
    return this.$http({
      method: 'PUT',
      data,
      url: `${this.api}/aid/requests/${ticketId}/closedate`,
    }).then(DataResponse.transform('data.id'));
  }

  lock(ticketId) {
    return this.$http({
      method: 'PUT',
      url: `${this.api}/aid/requests/${ticketId}/lock`,
    }).then(DataResponse.transform('data'));
  }

  release(ticketId) {
    return this.$http({
      method: 'DELETE',
      url: `${this.api}/aid/requests/${ticketId}/lock`,
    }).then(DataResponse.transform('data'));
  }

  reject(ticketId, data) {
    return this.$http({
      method: 'PUT',
      data,
      url: `${this.api}/aid/requests/${ticketId}/reject`,
    }).then(DataResponse.transform('data.id'));
  }

  close(ticketType, ticketId) {
    // TODO THIS IS UGLY AND NEEDS REWORK!
    // Fix the annoying backend paths:
    // incoming: DELETE /api/v0/aid/requests/{aggregateId}
    // outgoing: DELETE /api/v0/aid/requests/outgoing/{aggregateId}
    let typePathFix = ticketType === 'incoming' ? '' : `${ticketType}/`;
    return this.$http({
      method: 'DELETE',
      url: `${this.api}/aid/requests/${typePathFix}${ticketId}`,
    });
  }

  notify(ticketId, ticketType = 'outgoing') {
    return this.$http({
      method: 'POST',
      url: `${this.api}/aid/requests/${ticketType}/${ticketId}/notify`,
    });
  }

  requestCanceler(handlerName, resolved = false) {
    // Remove actual resolved request handler
    if (resolved) {
      return delete this[handlerName];
    }

    // Break currently running request
    if (this[handlerName]) {
      this[handlerName].resolve();
    }

    // Create new request handler
    this[handlerName] = this.$q.defer();
    return this[handlerName];
  }

  all(ticketType) {
    return this.filter(ticketType, null, 'aidRequests', 'allReq');
  }

  filter(ticketType, params, product) {
    const type = this.getTicketType(ticketType);
    let url = '';
    switch (product) {
      case ProductType.DWS:
        url = `${this.apiWithDivergentVersion('v2')}/dws/requests/${type}`;
        break;
      case ProductType.KWS:
        url = `${this.apiWithDivergentVersion('v2')}/kws/requests/${type}`;
        break;
      case ProductType.KWH:
        url = `${this.apiWithDivergentVersion('v2')}/aid/requests/${type}`;
        break;
      default:
        url = `${this.apiWithDivergentVersion('v1')}/aid/requests/${type}`;
    }
    let request = {
      method: 'GET',
      url,
      error: { silent: true },
    };

    if (params) {
      request.params = params;
    }

    return this.$http(request).then(DataResponse.transform('data'));
  }

  one(ticketId, product = null) {
    let url = '';
    let transform = 'data.aidRequest';
    switch (product) {
      case ProductType.DWS:
        url = `${this.apiWithDivergentVersion('v0')}/dws/requests/${ticketId}`;
        transform = 'data';
        break;
      case ProductType.KWS:
        url = `${this.apiWithDivergentVersion('v0')}/kws/requests/${ticketId}`;
        break;
      default:
        url = `${this.apiWithDivergentVersion('v0')}/aid/requests/${ticketId}`;
    }

    return this.$http({
      method: 'GET',
      url,
    }).then(DataResponse.transform(transform));
  }

  completeDWS(ticketId) {
    return this.$http({
      method: 'POST',
      url: `${this.api}/dws/requests/${ticketId}/complete`,
    });
  }

  cancelDWS(ticketId) {
    return this.$http({
      method: 'POST',
      url: `${this.api}/dws/requests/${ticketId}/cancel`,
    });
  }

  action(ticketId, data) {
    return this.$http({
      method: 'POST',
      url: `${this.api}/aid/requests/${ticketId}`,
      data,
    });
  }

  addComment(ticketId, data) {
    return this.$http({
      method: 'POST',
      url: `${this.api}/requests/${ticketId}/comment`,
      data,
    });
  }

  fetchTransactions(ticketType, ticketId) {
    const type = this.getTicketType(ticketType);
    return this.$http({
      method: 'GET',
      url: `${this.api}/aid/requests/${type}/${ticketId}/finances`,
    }).then(DataResponse.transform('data'));
  }

  putFinances(ticketId, data) {
    return this.$http({
      method: 'PUT',
      data,
      url: `${this.api}/aid/requests/${ticketId}/finances`,
    }).then(DataResponse.transform('data'));
  }

  bufferFinances(ticketId, data) {
    return this.$http({
      method: 'PUT',
      data,
      url: `${this.api}/aid/requests/${ticketId}/bufferFinances`,
    }).then(DataResponse.transform('data'));
  }

  deleteUploadedBalanceList(ticketId) {
    return this.$http({
      method: 'POST',
      url: `${this.api}/aid/requests/incoming/${ticketId}/resetUploadedBalanceList`,
    }).then(DataResponse.transform('data'));
  }

  putFinancesConfirm(ticketId, data) {
    return this.$http({
      method: 'PUT',
      data,
      url: `${this.api}/aid/requests/${ticketId}/financesConfirm`,
    }).then(DataResponse.transform('data'));
  }

  putFinancesOutgoing(ticketId, finances) {
    return this.$http({
      method: 'PUT',
      data: finances,
      url: `${this.api}/aid/requests/outgoing/${ticketId}/finances`,
    }).then(DataResponse.transform('data'));
  }

  postStandingOrders(ticketId, standingOrders) {
    return this.$http({
      method: 'POST',
      data: standingOrders,
      url: `${this.api}/aid/requests/outgoing/${ticketId}/finances/standingOrders`,
    });
  }

  getWarrantUrl(ticketId) {
    return `${this.api}/aid/requests/${ticketId}/warrantForm`;
  }

  getSignedWarrantUrl(ticketId, force) {
    let url = `${this.api}/aid/signedWarrant`;
    if (ticketId) {
      url += `/${ticketId}`;
    }
    if (force) {
      url += '?force=true';
    }
    return url;
  }

  uploadSignedWarrant(file, ticketId, force = false) {
    return this.Upload.upload({
      url: this.getSignedWarrantUrl(ticketId, force),
      data: { file },
    }).then(DataResponse.transform('data'));
  }

  uploadSterbeurkunde(file, ticketId) {
    return this.Upload.upload({
      url: `${this.api}/aid/signedWarrant/${ticketId}/addDeathCertificate`,
      data: { file },
    });
  }

  uploadSignedWarrantOutgoing(file) {
    return this.Upload.upload({
      url: this.getSignedWarrantUrl(),
      data: { file },
    }).then(DataResponse.transform('data'));
  }

  uploadReceivedPayments(ticketId, file) {
    return this.Upload.upload({
      url: `${this.api}/aid/requests/${ticketId}/documents/receivedPayments`,
      data: { file },
    });
  }

  uploadSepaDirectDebits(ticketId, file) {
    return this.Upload.upload({
      url: `${this.api}/aid/requests/${ticketId}/documents/sepaDirectDebits`,
      data: { file },
    });
  }

  uploadAllTransactionsParallelZiraat(
    ticketId,
    partners,
    standingOrders,
    transactions,
  ) {
    const data = {
      'partnersSPDIL.xlsx': partners,
      'transactionsCHARE.xlsx': transactions,
    };

    if (!_.isEmpty(standingOrders)) {
      data['standingOrdersPAYSI.xlsx'] = standingOrders;
    }

    return this.Upload.upload({
      url: `${this.api}/aid/requests/${ticketId}/documents/balanceList`,
      data,
    });
  }

  uploadStandingOrders(ticketId, file) {
    return this.Upload.upload({
      url: `${this.api}/aid/requests/${ticketId}/documents/standingOrders`,
      data: { file },
    });
  }

  uploadScheduledTransaction(ticketId, file) {
    return this.Upload.upload({
      url: `${this.api}/aid/requests/${ticketId}/documents/scheduledTransaction`,
      data: { file },
    });
  }

  /**
   * Upload a balanceList to a ticket, identified by its id
   *
   * This method has a second, optional parameter "additionalFile". It must only be used in conjunction with uploadHandlers,
   * that require such an additional file
   *
   * @param {UUID} ticketId
   * @param {File} file
   * @param {File} additionalFile (optional)
   */
  uploadBalanceList(ticketId, file, additionalFile) {
    return this.Upload.upload({
      url: `${this.api}/aid/requests/${ticketId}/documents/balanceList`,
      data: { file, additionalFile },
    });
  }

  uploadTransactionData(ticketId, file) {
    return this.Upload.upload({
      url: `${this.api}/aid/requests/${ticketId}/documents/accountSalesData`,
      data: { file },
    });
  }

  uploadMisc(ticketId, file) {
    return this.Upload.upload({
      url: `${this.api}/aid/requests/${ticketId}/documents/other`,
      data: { file },
    });
  }

  uploadRejection(ticketId, file, entryDate) {
    return this.Upload.upload({
      url: `${this.api}/aid/requests/incoming/${ticketId}/reject/document`,
      data: { file, entryDate },
    });
  }

  rejectReceiving(ticketId) {
    return this.$http({
      method: 'PUT',
      url: `${this.api}/aid/requests/incoming/${ticketId}/reject`,
    });
  }

  skipDownloadWarrentForm(ticketId) {
    return this.$http({
      method: 'GET',
      url: this.getWarrantUrl(ticketId),
    });
  }

  redirectWarrantForm(ticketId) {
    return this.$http({
      method: 'GET',
      url: this.getWarrantUrl(ticketId),
    }).then((file) => this.fileResponseService.redirect(file));
  }

  getWarrantWithSignature(ticketId, data) {
    return this.$http({
      method: 'PUT',
      url: this.getWarrantUrl(ticketId),
      data,
    });
  }

  downloadStandingOrdersCsv(ticketId) {
    return this.$http({
      method: 'GET',
      url: `${this.api}/aid/requests/${ticketId}/standingordercsv`,
      responseType: 'arraybuffer',
    }).then(FileResponse.transform);
  }

  downloadStandingOrdersJson(ticketId) {
    const url = `/api/v0/aid/requests/incoming/${ticketId}/finances/standingOrders/export`;
    return this.$http({
      method: 'GET',
      url,
      responseType: 'arraybuffer',
    }).then(FileResponse.transform);
  }

  downloadCsvConfig() {
    return this.$http({
      method: 'GET',
      url: `${this.api}/aid/requests/configcsv`,
      responseType: 'arraybuffer',
    }).then(FileResponse.transform);
  }

  redirectSignedWarrantForm(ticketId, type = 'redirect') {
    return this.$http({
      method: 'GET',
      url: this.getSignedWarrantUrl(ticketId),
      params: { type },
    }).then((file) => this.fileResponseService.redirect(file));
  }

  downloadDocument(url) {
    return this.$http({
      method: 'GET',
      url: url,
      responseType: 'arraybuffer',
    }).then(FileResponse.transform);
  }

  redirectDocument(url, type = 'redirect') {
    return this.$http({
      method: 'GET',
      url: url,
      params: { type },
    }).then((file) => this.fileResponseService.redirect(file));
  }

  completeOutgoing(ticketId) {
    return this.$http({
      method: 'POST',
      url: `${this.api}/aid/requests/outgoing/${ticketId}/complete`,
    });
  }

  completeIncoming(ticketId) {
    return this.$http({
      method: 'POST',
      url: `${this.api}/aid/requests/incoming/${ticketId}/complete`,
    });
  }

  outgoingDataComplete(ticketId) {
    return this.$http({
      method: 'POST',
      url: `${this.api}/aid/requests/${ticketId}/documents/confirmation`,
    });
  }

  transactionDataSkip(ticketId) {
    return this.$http({
      method: 'POST',
      url: `${this.api}/aid/requests/${ticketId}/transactionDataSkip`,
    });
  }

  rejectSignedWarrant(ticketId, reasons) {
    return this.$http({
      method: 'PUT',
      data: reasons,
      url: `${this.api}/aid/signedwarrant/${ticketId}/reject`,
    });
  }

  initializationConfirmation(ticketId, data) {
    return this.$http({
      method: 'POST',
      url: `${this.api}/aid/requests/${ticketId}/initializationConfirmation`,
      data,
    });
  }

  initializationConfirmationByCustomer(ticketId, data) {
    return this.$http({
      method: 'POST',
      url: `${this.api}/aid/requests/${ticketId}/initializationConfirmationByCustomer`,
      data,
    });
  }

  putStandingOrder(ticketId, standingOrderId, data) {
    return this.$http({
      method: 'PUT',
      data,
      url: `${this.api}/aid/requests/${ticketId}/finances/standingOrders/${standingOrderId}`,
    }).then(DataResponse.transform('data'));
  }

  processStandingOrdersWithRemark(ticketId, data) {
    return this.$http({
      method: 'PUT',
      data,
      url: `${this.api}/aid/requests/incoming/${ticketId}/processStandingOrders`,
    });
  }

  processStandingOrders(ticketId) {
    return this.$http({
      method: 'PUT',
      url: `${this.api}/aid/requests/incoming/${ticketId}/processStandingOrders`,
    });
  }

  updateStandingOrderStatus(ticketId, standingOrderId, data) {
    return this.$http({
      method: 'PUT',
      data,
      url: `${this.api}/aid/requests/incoming/${ticketId}/finances/standingOrders/${standingOrderId}/status`,
    });
  }

  updatePaymentPartner(ticketId, data) {
    return this.$http({
      method: 'PUT',
      data,
      url: `${this.api}/aid/requests/incoming/${ticketId}/finances/updatePaymentPartner`,
    });
  }

  createFactSheet(ticketId) {
    return this.$http({
      method: 'GET',
      url: `${this.api}/aid/requests/factSheet/${ticketId}`,
    });
  }

  updateCustomer(ticketId, data, ticketType) {
    return this.$http({
      method: 'PUT',
      data: data,
      url: `${this.api}/aid/requests/${ticketId}/customer?ticketType=${ticketType}`,
    });
  }

  updateIban(ticketId, iban) {
    return this.$http({
      method: 'POST',
      data: iban,
      url: `${this.api}/aid/requests/outgoing/${ticketId}/updateIban`,
    });
  }

  accountSwitchPackage(ticketId, ticketType) {
    const type = this.getTicketType(ticketType);
    return this.$http({
      method: 'GET',
      url: `${this.api}/aid/requests/${type}/${ticketId}/accountSwitchPackage`,
      responseType: 'arraybuffer',
    }).then(FileResponse.transform);
  }

  hide(ticketId, ticketType) {
    const type = this.getTicketType(ticketType);
    return this.$http({
      method: 'POST',
      url: `${this.api}/aid/requests/${type}/${ticketId}/hide`,
    });
  }

  resendDocument(ticketId, ticketType, documentType, documentId) {
    const type = this.getTicketType(ticketType);
    return this.$http({
      method: 'GET',
      url: `${this.api}/aid/requests/${type}/${ticketId}/documents/${documentType}/${documentId}/resend`,
    });
  }

  informPaymentPartners(ticketId) {
    return this.$http({
      method: 'POST',
      url: `${this.api}/aid/requests/incoming/${ticketId}/informPaymentPartners`,
    });
  }

  // the legacy way
  simulateFinancesDataCaptured(ticketId) {
    return this.$http({
      method: 'POST',
      url: `${this.api}/aid/requests/${ticketId}/debug/financeData`,
    });
  }

  changeEmployeeStatus(ticketId, ticketType, data) {
    const type = this.getTicketType(ticketType);
    return this.$http({
      method: 'POST',
      url: `${this.api}/aid/requests/${type}/${ticketId}/isCustomerTenantEmployee`,
      data,
    });
  }

  resetToSentRequestToTransferring(ticketId) {
    return this.$http({
      method: 'POST',
      url: `${this.api}/aid/requests/${ticketId}/ops/resetToSentRequestToTransferring`,
    });
  }

  uploadAdditionalDocuments(ticketId, file) {
    return this.Upload.upload({
      url: `${this.api}/aid/signedWarrant/${ticketId}/additionalDocuments`,
      data: { file },
    });
  }

  pauseReminders(ticketId) {
    return this.$http({
      method: 'POST',
      url: `${this.api}/aid/incoming/${ticketId}/reminders/pause`,
    });
  }

  downloadArchiveManually(ticketId) {
    return this.$http({
      method: 'GET',
      url: `${this.api}/archiveAtruvia/${ticketId}/document`,
    }).then(DataResponse.transform('data'));
  }

  markAsArchivedManually(ticketId) {
    return this.$http({
      method: 'GET',
      url: `${this.api}/archiveAtruvia/${ticketId}/manuelArchived`,
    }).then(DataResponse.transform('data'));
  }

  resumeReminders(ticketId) {
    return this.$http({
      method: 'POST',
      url: `${this.api}/aid/incoming/${ticketId}/reminders/resume`,
    });
  }
}
